import * as React from 'react'

import { CLIENT_CLINICAL_PAGE_SIZE } from '@/config/constants'
import {
  mapChronicCondition,
  mapLabResult,
} from '@/features/clinical-data/api/api'
import { useClinicalDataQuery } from '@/features/clinical-data/hooks/useClinicalDataQuery'
import type {
  ChronicConditionRecord,
  LabResultRecord,
} from '@/features/clinical-data/types'
import { useCapability } from '@/features/permissions/hooks'
import { useSlice, type HistorySlice } from './useClientHistory'

export interface ClientClinical {
  conditions: HistorySlice<ChronicConditionRecord>
  labs: HistorySlice<LabResultRecord>
}

/**
 * The client's chronic conditions and lab results, newest first. Each needs
 * its own capability, which no role holds by default, so most viewers get
 * neither and the sheet leaves the Clinical tab out.
 */
export function useClientClinical(
  patientId: string | undefined
): ClientClinical {
  const canConditions = useCapability('data.chronicConditions.view') === true
  const canLabs = useCapability('data.labResults.view') === true

  const params = React.useMemo(
    () => ({
      limit: CLIENT_CLINICAL_PAGE_SIZE,
      filters: patientId
        ? [
            {
              id: 'patient_id',
              field: 'patient_id',
              operator: '=' as const,
              value: patientId,
            },
          ]
        : [],
    }),
    [patientId]
  )

  const conditionsQuery = useClinicalDataQuery({
    endpoint: 'chronic-conditions',
    params,
    mapRow: mapChronicCondition,
    enabled: canConditions && !!patientId,
  })
  const labsQuery = useClinicalDataQuery({
    endpoint: 'lab-results',
    params,
    mapRow: mapLabResult,
    enabled: canLabs && !!patientId,
  })

  const conditions = useSlice(
    canConditions,
    conditionsQuery,
    conditionsQuery.data?.pages[0]?.data,
    conditionsQuery.hasNextPage
  )
  const labs = useSlice(
    canLabs,
    labsQuery,
    labsQuery.data?.pages[0]?.data,
    labsQuery.hasNextPage
  )
  return React.useMemo(() => ({ conditions, labs }), [conditions, labs])
}
